import { useMemo } from 'react';
import type { Metro } from '../types/metro';
import { RATIO_RANGE_BOUNDS, useFilterStore } from '../stores/filterStore';
import { lensedStatusForMetro, metroMatchesOccChip } from '../lib/occupation-utils';

/**
 * Apply the cross-page filter store (search, statuses, ratio range,
 * occupational group) to a list of metros. The output is memoized so
 * consumers can pass it directly to map markers and list items without
 * thrashing on re-renders.
 *
 * The occupational-group filter narrows visible metros to those that
 * CARRY the named occupational group — either status — per the metro's
 * real `occupationGroups` data (PR #23 backend ingest; CR-039 widened
 * the old shortage-only rule that hid every group-surplus metro, per
 * Frank's 2026-09-17 report — DEC-081). The chip ↔ group mapping
 * lives in `lib/occupation-utils.ts` so the chip ids stay decoupled
 * from the display names.
 *
 * While an OG lens is active, the STATUS filter classifies each metro
 * by the LENSED status (`lensedStatusForMetro` — the selected group's
 * own status) so the pills agree with the group figures the dots/rows
 * actually display; with no lens it keeps using the metro-level
 * status, unchanged.
 *
 * Ratio range (2026-07-02, Georgetown feedback): the Map page now
 * exposes a dual-thumb slider that writes to `filterStore.ratioRange`
 * (a [lo, hi] tuple in [0, 2]). Rankings / Detail / Providers still
 * render the binary shortage/surplus chips, so we AND both predicates
 * here: a metro must pass BOTH the status set AND the ratio window.
 * When the slider sits at the default bounds ([0, 2]), the ratio check
 * is a no-op — which keeps behavior on the other pages identical to
 * pre-slider.
 *
 * Step 2 (PR #25): the previous `sectorMap` precompute that fed
 * `getSectorData()` into `metroMatchesOccChip` is gone — the new
 * implementation reads directly from `metro.occupationGroups`, which
 * is already in memory, so there's no seed-math recompute to amortize.
 */
export function useFilteredMetros(
  metros: ReadonlyArray<Metro>,
): ReadonlyArray<Metro> {
  const search = useFilterStore((s) => s.search);
  const statuses = useFilterStore((s) => s.statuses);
  const ratioRange = useFilterStore((s) => s.ratioRange);
  const occupationalGroupId = useFilterStore((s) => s.occupationalGroupId);

  return useMemo(() => {
    const q = search.trim().toLowerCase();
    const [rLo, rHi] = ratioRange;
    // Skip the numeric bounds check entirely when the slider is at its
    // default [0, 2] window -- saves two comparisons per metro on the
    // pages that don't render the slider at all.
    const rangeIsDefault = rLo === RATIO_RANGE_BOUNDS[0] && rHi === RATIO_RANGE_BOUNDS[1];
    return metros.filter((m) => {
      if (!statuses.has(lensedStatusForMetro(m, occupationalGroupId))) return false;
      if (!rangeIsDefault && (m.ratio < rLo || m.ratio > rHi)) return false;
      if (q && !m.name.toLowerCase().includes(q)) return false;
      if (!metroMatchesOccChip(m, occupationalGroupId)) return false;
      return true;
    });
  }, [metros, search, statuses, ratioRange, occupationalGroupId]);
}
